import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const screens = [
  {
    role: 'Public / All Users',
    color: 'from-amber-500 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-800',
    iconColor: 'text-amber-600',
    icon: 'Globe',
    screens: [
      { name: 'Home', path: '/', icon: 'Home', desc: 'Landing page with platform overview' },
      { name: 'Sugar Marketplace', path: '/sugar-marketplace-dashboard', icon: 'ShoppingCart', desc: 'Browse & book sugar loads' },
      { name: 'Truck Marketplace', path: '/truck-marketplace', icon: 'Truck', desc: 'Find & book transport vehicles' },
      { name: 'Live Shipment Tracking', path: '/live-shipment-tracking', icon: 'MapPin', desc: 'Real-time shipment location' },
      { name: 'News & Market Prices', path: '/news-and-market-prices', icon: 'TrendingUp', desc: 'Sugar price trends & news' },
      { name: 'Sugar Order Request Form', path: '/sugar-order-request-form', icon: 'FileText', desc: 'Shareable order request form' },
      { name: 'Login / Authentication', path: '/login-authentication', icon: 'LogIn', desc: 'Role-based login portal' },
    ],
  },
  {
    role: 'Buyer',
    color: 'from-blue-500 to-cyan-500',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-800',
    iconColor: 'text-blue-600',
    icon: 'ShoppingBag',
    screens: [
      { name: 'Buyer Operations Dashboard', path: '/buyer-operations-dashboard', icon: 'LayoutDashboard', desc: 'Bookings, invoices & tracking' },
    ],
  },
  {
    role: 'Factory',
    color: 'from-green-500 to-emerald-500',
    bg: 'bg-green-50',
    border: 'border-green-200',
    badge: 'bg-green-100 text-green-800',
    iconColor: 'text-green-600',
    icon: 'Factory',
    screens: [
      { name: 'Quota Manager Dashboard', path: '/factory-quota-manager-dashboard', icon: 'BarChart2', desc: 'Factory analytics & quota overview' },
      { name: 'Factory Registration & Profile', path: '/factory-registration-profile-management', icon: 'Building2', desc: 'Onboard & manage factory profiles' },
      { name: 'Quota & Pricing Center', path: '/quota-allocation-pricing-center', icon: 'DollarSign', desc: 'Set grades, pricing & allocation' },
      { name: 'Factory Transporter Management', path: '/factory-transporter-management', icon: 'Truck', desc: 'Manage transporter network' },
      { name: 'Bulk Data Upload Center', path: '/bulk-data-upload-center', icon: 'Upload', desc: 'CSV/Excel bulk data import' },
      { name: 'Factory Management Hub', path: '/factory-management-hub', icon: 'Settings', desc: 'Full factory operations hub' },
    ],
  },
  {
    role: 'Transporter',
    color: 'from-purple-500 to-violet-500',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    badge: 'bg-purple-100 text-purple-800',
    iconColor: 'text-purple-600',
    icon: 'Truck',
    screens: [
      { name: 'Transporter Dashboard', path: '/transporter-dashboard', icon: 'LayoutDashboard', desc: 'Trips, bids & vehicle status' },
      { name: 'Driver Login', path: '/driver-authentication', icon: 'LogIn', desc: 'Driver authentication portal' },
      { name: 'Driver Registration & Profile', path: '/driver-registration-profile', icon: 'UserCheck', desc: 'Driver onboarding & documents' },
    ],
  },
  {
    role: 'Admin / Manager',
    color: 'from-rose-500 to-red-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    badge: 'bg-rose-100 text-rose-800',
    iconColor: 'text-rose-600',
    icon: 'Shield',
    screens: [
      { name: 'Admin Control Panel', path: '/admin-control-panel', icon: 'ShieldCheck', desc: 'KYC, payments & system health' },
      { name: 'Manager Login', path: '/manager-authentication', icon: 'LogIn', desc: 'Manager authentication portal' },
      { name: 'Manager Dashboard', path: '/manager-dashboard', icon: 'BarChart2', desc: 'Operational oversight & reports' },
      { name: 'Reports Generation Center', path: '/reports-generation-center', icon: 'FileBarChart', desc: 'Generate & export reports' },
    ],
  },
];

const AppSitemap = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const totalScreens = screens?.reduce((acc, r) => acc + r?.screens?.length, 0);

  const filtered = search?.trim()
    ? screens?.map(role => ({
        ...role,
        screens: role?.screens?.filter(
          s =>
            s?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
            s?.desc?.toLowerCase()?.includes(search?.toLowerCase())
        ),
      }))?.filter(r => r?.screens?.length > 0)
    : screens;

  return (
    <div className="min-h-screen bg-gray-950 text-white" style={{ background: '#0f1f38', fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
              <Icon name="Map" size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white leading-tight">SugarHathi App Navigator</h1>
              <p className="text-xs text-gray-400">{totalScreens} screens across {screens?.length} roles</p>
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-800"
          >
            <Icon name="ArrowLeft" size={15} />
            Back
          </button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="relative max-w-md mb-8">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search screens..."
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
          />
        </div>

        {/* Role sections */}
        <div className="space-y-8">
          {filtered?.map(role => (
            <div key={role?.role}>
              {/* Role header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${role?.color} flex items-center justify-center`}>
                  <Icon name={role?.icon} size={15} className="text-white" />
                </div>
                <h2 className="text-base font-semibold text-white">{role?.role}</h2>
                <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">
                  {role?.screens?.length} screen{role?.screens?.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Screen cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {role?.screens?.map(screen => (
                  <button
                    key={screen?.path}
                    onClick={() => navigate(screen?.path)}
                    className="group text-left bg-gray-900 border border-gray-800 rounded-xl p-4 hover:border-gray-600 hover:bg-gray-800 transition-all duration-200 hover:shadow-lg hover:shadow-black/30 hover:-translate-y-0.5"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${role?.color} bg-opacity-10 flex items-center justify-center flex-shrink-0 opacity-80 group-hover:opacity-100 transition-opacity`}>
                        <Icon name={screen?.icon} size={16} className="text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white leading-snug group-hover:text-amber-300 transition-colors">
                          {screen?.name}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{screen?.desc}</p>
                        <p className="text-xs text-gray-600 mt-1.5 font-mono truncate group-hover:text-gray-400 transition-colors">
                          {screen?.path}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-1 text-xs text-gray-600 group-hover:text-amber-400 transition-colors">
                      <span>Open screen</span>
                      <Icon name="ArrowRight" size={12} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {filtered?.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              <Icon name="SearchX" size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">No screens match "{search}"</p>
            </div>
          )}
        </div>

        {/* Footer note */}
        <div className="mt-12 pt-6 border-t border-gray-800 text-center">
          <p className="text-xs text-gray-600">SugarHathi · {totalScreens} screens · 5 roles · Internal Navigator</p>
        </div>
      </div>
    </div>
  );
};

export default AppSitemap;
